/**
 * UpgradeHistoryModal Component
 *
 * Displays the upgrade history for a specific tenant.
 *
 * @module features/tenants/components/modals/UpgradeHistoryModal
 */

import React from 'react';
import { RefreshIcon } from '../../../../components/common/Icons';
import type { Tenant } from '../../types';
import type { UpgradeHistoryItem } from '../../api/upgradeApi';

interface UpgradeHistoryModalProps {
  isOpen: boolean;
  tenant: Tenant | null;
  history: UpgradeHistoryItem[];
  loading: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

function statusColor(status: string): string {
  switch (status) {
    case 'completed': return 'active';
    case 'failed': return 'suspended';
    case 'rolled_back': return 'archived';
    default: return 'provisioning';
  }
}

export const UpgradeHistoryModal: React.FC<UpgradeHistoryModalProps> = ({
  isOpen,
  tenant,
  history,
  loading,
  onClose,
  onRefresh,
}) => {
  if (!isOpen || !tenant) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>Upgrade History: {tenant.name}</h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
            <button className="btn btn-sm btn-secondary" onClick={onRefresh} disabled={loading}>
              <RefreshIcon size={14} /> Refresh
            </button>
          </div>

          {loading && history.length === 0 && (
            <div className="loading-container">
              <div className="loading-spinner" />
              <p>Loading history...</p>
            </div>
          )}

          {!loading && history.length === 0 && (
            <div className="empty-state">
              <p>No upgrade history for this tenant.</p>
            </div>
          )}

          {history.length > 0 && (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>From</th>
                    <th>To</th>
                    <th>Status</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td>{item.from_version}</td>
                      <td>{item.to_version}</td>
                      <td>
                        <span className={`status-badge status-${statusColor(item.status)}`}>
                          {item.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {item.started_at ? new Date(item.started_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ fontSize: '12px' }}>
                        {item.completed_at ? new Date(item.completed_at).toLocaleString() : '—'}
                      </td>
                      <td style={{ fontSize: '12px' }}>{item.initiated_by || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
